export interface Locale {
  code: string;
  name: string;
  nativeName: string;
}

/**
 * Supported locales - must match backend config/locales.php
 * This is the single source of truth for supported languages in the frontend
 */
export const SUPPORTED_LOCALES: Locale[] = [
  { code: 'en', name: 'English', nativeName: 'English' },
  { code: 'es', name: 'Spanish', nativeName: 'Español' },
  { code: 'fr', name: 'French', nativeName: 'Français' },
  { code: 'de', name: 'German', nativeName: 'Deutsch' },
  { code: 'it', name: 'Italian', nativeName: 'Italiano' },
  { code: 'pt', name: 'Portuguese', nativeName: 'Português' },
  { code: 'ru', name: 'Russian', nativeName: 'Русский' },
  { code: 'ar', name: 'Arabic', nativeName: 'العربية' },
  { code: 'nl', name: 'Dutch', nativeName: 'Nederlands' },
  { code: 'sv', name: 'Swedish', nativeName: 'Svenska' },
  { code: 'nb', name: 'Norwegian', nativeName: 'Norsk bokmål' },
  { code: 'da', name: 'Danish', nativeName: 'Dansk' },
  { code: 'fi', name: 'Finnish', nativeName: 'Suomi' },
  { code: 'he', name: 'Hebrew', nativeName: 'עברית' },
  { code: 'cs', name: 'Czech', nativeName: 'Čeština' },
  { code: 'pl', name: 'Polish', nativeName: 'Polski' },
  { code: 'hi', name: 'Hindi', nativeName: 'हिन्दी' },
  { code: 'tr', name: 'Turkish', nativeName: 'Türkçe' },
  { code: 'uk', name: 'Ukrainian', nativeName: 'Українська' },
  { code: 'ro', name: 'Romanian', nativeName: 'Română' },
  { code: 'id', name: 'Indonesian', nativeName: 'Bahasa Indonesia' },
  { code: 'ja', name: 'Japanese', nativeName: '日本語' },
  { code: 'ko', name: 'Korean', nativeName: '한국어' },
  { code: 'zh', name: 'Chinese', nativeName: '中文' },
];

/**
 * Locale codes excluded from album generation
 */
const ALBUM_EXCLUDED_LOCALES = new Set(['hi']);

/**
 * Locales available for album generation — subset of SUPPORTED_LOCALES
 */
export const ALBUM_LOCALES: Locale[] = SUPPORTED_LOCALES.filter(
  (l) => !ALBUM_EXCLUDED_LOCALES.has(l.code)
);

/**
 * Sort album locales with user's locale first, then English, then alphabetically
 */
export function sortAlbumLocalesForUser(userLocale?: string): Locale[] {
  const sorted = [...ALBUM_LOCALES];

  return sorted.sort((a, b) => {
    if (userLocale) {
      if (a.code === userLocale) return -1;
      if (b.code === userLocale) return 1;
    }
    if (a.code === 'en') return -1;
    if (b.code === 'en') return 1;
    return a.name.localeCompare(b.name);
  });
}

export const DEFAULT_LOCALE = 'en';
export const FALLBACK_LOCALE = 'en';

/**
 * Check if locale is valid
 */
export function isValidLocale(locale: string): boolean {
  return SUPPORTED_LOCALES.some((l) => l.code === locale);
}

/**
 * Get locale by code
 */
export function getLocaleByCode(code: string): Locale | undefined {
  return SUPPORTED_LOCALES.find((locale) => locale.code === code);
}

/**
 * Sort locales with user's locale first, then English, then alphabetically
 */
export function sortLocalesForUser(userLocale?: string): Locale[] {
  const sorted = [...SUPPORTED_LOCALES];

  return sorted.sort((a, b) => {
    // User's locale comes first
    if (userLocale) {
      if (a.code === userLocale) return -1;
      if (b.code === userLocale) return 1;
    }

    // English comes second
    if (a.code === 'en') return -1;
    if (b.code === 'en') return 1;

    // Rest sorted alphabetically by name
    return a.name.localeCompare(b.name);
  });
}

/**
 * RTL (Right-To-Left) language codes
 */
export const RTL_LOCALES = ['ar', 'he'] as const;

/**
 * Check if locale uses RTL (Right-To-Left) direction
 */
export function isRtlLocale(locale: string): boolean {
  return RTL_LOCALES.includes(locale as (typeof RTL_LOCALES)[number]);
}
